/**
 * getThemeCSS — Generate CSS variable block for visualizer iframe.
 *
 * Produces the full design system CSS (variables + SVG classes) that gets
 * injected into the sandboxed iframe before the visualization content.
 */

export type ThemeMode = 'light' | 'dark';

const LIGHT_VARS = `
  --bg-primary: #ffffff;
  --bg-secondary: #f8fafc;
  --bg-tertiary: #f1f5f9;
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-tertiary: #94a3b8;
  --border-primary: #e2e8f0;
  --border-secondary: #cbd5e1;

  --purple-100: #f3e8ff; --purple-200: #e9d5ff; --purple-300: #d8b4fe;
  --purple-400: #c084fc; --purple-500: #a855f7; --purple-600: #9333ea; --purple-700: #7e22ce;
  --teal-100: #ccfbf1; --teal-200: #99f6e4; --teal-300: #5eead4;
  --teal-400: #2dd4bf; --teal-500: #14b8a6; --teal-600: #0d9488; --teal-700: #0f766e;
  --coral-100: #ffe4e6; --coral-200: #fecdd3; --coral-300: #fda4af;
  --coral-400: #fb7185; --coral-500: #f43f5e; --coral-600: #e11d48; --coral-700: #be123c;
  --pink-100: #fce7f3; --pink-200: #fbcfe8; --pink-300: #f9a8d4;
  --pink-400: #f472b6; --pink-500: #ec4899; --pink-600: #db2777; --pink-700: #be185d;
  --gray-100: #f1f5f9; --gray-200: #e2e8f0; --gray-300: #cbd5e1;
  --gray-400: #94a3b8; --gray-500: #64748b; --gray-600: #475569; --gray-700: #334155;
  --blue-100: #dbeafe; --blue-200: #bfdbfe; --blue-300: #93c5fd;
  --blue-400: #60a5fa; --blue-500: #3b82f6; --blue-600: #2563eb; --blue-700: #1d4ed8;
  --green-100: #dcfce7; --green-200: #bbf7d0; --green-300: #86efac;
  --green-400: #4ade80; --green-500: #22c55e; --green-600: #16a34a; --green-700: #15803d;
  --amber-100: #fef3c7; --amber-200: #fde68a; --amber-300: #fcd34d;
  --amber-400: #fbbf24; --amber-500: #f59e0b; --amber-600: #d97706; --amber-700: #b45309;
  --red-100: #fee2e2; --red-200: #fecaca; --red-300: #fca5a5;
  --red-400: #f87171; --red-500: #ef4444; --red-600: #dc2626; --red-700: #b91c1c;

  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-size-sm: 12px;
  --font-size-base: 14px;
  --font-size-lg: 16px;
  --font-size-xl: 18px;
  --font-size-2xl: 22px;
  --border-radius-sm: 4px;
  --border-radius-md: 8px;
  --border-radius-lg: 12px;
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 32px;
`;

const DARK_VARS = `
  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --bg-tertiary: #334155;
  --text-primary: #f1f5f9;
  --text-secondary: #94a3b8;
  --text-tertiary: #64748b;
  --border-primary: #334155;
  --border-secondary: #475569;

  --purple-100: #3b0764; --purple-200: #581c87; --purple-300: #7e22ce;
  --purple-400: #a855f7; --purple-500: #c084fc; --purple-600: #d8b4fe; --purple-700: #e9d5ff;
  --teal-100: #042f2e; --teal-200: #134e4a; --teal-300: #0f766e;
  --teal-400: #14b8a6; --teal-500: #2dd4bf; --teal-600: #5eead4; --teal-700: #99f6e4;
  --coral-100: #4c0519; --coral-200: #881337; --coral-300: #be123c;
  --coral-400: #f43f5e; --coral-500: #fb7185; --coral-600: #fda4af; --coral-700: #fecdd3;
  --pink-100: #500724; --pink-200: #831843; --pink-300: #be185d;
  --pink-400: #ec4899; --pink-500: #f472b6; --pink-600: #f9a8d4; --pink-700: #fbcfe8;
  --gray-100: #1e293b; --gray-200: #334155; --gray-300: #475569;
  --gray-400: #64748b; --gray-500: #94a3b8; --gray-600: #cbd5e1; --gray-700: #e2e8f0;
  --blue-100: #1e3a5f; --blue-200: #1e40af; --blue-300: #1d4ed8;
  --blue-400: #3b82f6; --blue-500: #60a5fa; --blue-600: #93c5fd; --blue-700: #bfdbfe;
  --green-100: #052e16; --green-200: #14532d; --green-300: #15803d;
  --green-400: #22c55e; --green-500: #4ade80; --green-600: #86efac; --green-700: #bbf7d0;
  --amber-100: #451a03; --amber-200: #78350f; --amber-300: #b45309;
  --amber-400: #f59e0b; --amber-500: #fbbf24; --amber-600: #fcd34d; --amber-700: #fde68a;
  --red-100: #450a0a; --red-200: #7f1d1d; --red-300: #b91c1c;
  --red-400: #ef4444; --red-500: #f87171; --red-600: #fca5a5; --red-700: #fecaca;

  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-size-sm: 12px;
  --font-size-base: 14px;
  --font-size-lg: 16px;
  --font-size-xl: 18px;
  --font-size-2xl: 22px;
  --border-radius-sm: 4px;
  --border-radius-md: 8px;
  --border-radius-lg: 12px;
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 32px;
`;

/** SVG helper classes for diagrams — must match the skill's reference */
const SVG_CLASSES = `
  .node-text, .th { font-family: var(--font-family); fill: var(--text-primary); font-size: var(--font-size-base); font-weight: 500; }
  .node-subtitle, .ts { font-family: var(--font-family); fill: var(--text-secondary); font-size: var(--font-size-sm); font-weight: 400; }
  .edge-line, .arr { stroke: var(--border-secondary); stroke-width: 0.5; fill: none; }
  .edge-arrow { fill: var(--border-secondary); }
  svg text { font-family: var(--font-family); }

  /* Node color classes: .node.c-{ramp} sets rect fill + stroke from color ramps */
  .node.c-purple rect { fill: var(--purple-100); stroke: var(--purple-300); }
  .node.c-teal rect { fill: var(--teal-100); stroke: var(--teal-300); }
  .node.c-coral rect { fill: var(--coral-100); stroke: var(--coral-300); }
  .node.c-pink rect { fill: var(--pink-100); stroke: var(--pink-300); }
  .node.c-gray rect { fill: var(--gray-100); stroke: var(--gray-300); }
  .node.c-blue rect { fill: var(--blue-100); stroke: var(--blue-300); }
  .node.c-green rect { fill: var(--green-100); stroke: var(--green-300); }
  .node.c-amber rect { fill: var(--amber-100); stroke: var(--amber-300); }
  .node.c-red rect { fill: var(--red-100); stroke: var(--red-300); }
`;

/**
 * Generates the full CSS block to inject into a visualizer iframe.
 *
 * @param mode - Theme mode. Defaults to 'light' (dark mode not active in legalworkspace).
 * @returns CSS string ready for injection in a <style> tag
 */
export function getThemeCSS(mode: ThemeMode = 'light'): string {
  const vars = mode === 'dark' ? DARK_VARS : LIGHT_VARS;
  return `
    :root { ${vars} }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: var(--font-family);
      color: var(--text-primary);
      background: transparent;
      line-height: 1.5;
    }
    ${SVG_CLASSES}
  `;
}
